import React, { createContext, PropsWithChildren, useState, useCallback, SetStateAction, Dispatch } from 'react'
import type { CustomerSortOptions } from '../../../../../../../graphql/api/types'

type Header = {
  data: {
    sort: CustomerSortOptions
    search: string
  }
  setters: {
    setSort: (options: CustomerSortOptions) => void
    setSearch: (search: string) => void
  }
}

type ListFieldsErrorState = {
  value: Error | null
  refetch: () => Promise<unknown>
}

type ListFieldsContextType = {
  header: Header
  error: {
    value: Error | null
    refetch: () => Promise<unknown>
    set: (error: Omit<ListFieldsContextType['error'], 'set'>) => void
  }
  setSelectedFieldId: Dispatch<SetStateAction<string>>
  selectedFieldId: string
  setIsZoomedOut: Dispatch<SetStateAction<boolean>>
  isZoomedOut: boolean
}

const INITIAL_DATA: ListFieldsContextType = {
  header: {
    data: {
      search: '',
      sort: (localStorage.getItem('FIELD_PREFERRED_SORT_OPTION') as CustomerSortOptions) || 'MOST_RECENT',
    },
    setters: {
      setSort: () => undefined,
      setSearch: () => undefined,
    },
  },
  error: {
    value: null,
    refetch: async () => undefined,
    set: () => undefined,
  },
  setSelectedFieldId: () => undefined,
  selectedFieldId: '',
  setIsZoomedOut: () => undefined,
  isZoomedOut: false,
}

export const ListFieldsContext = createContext<ListFieldsContextType>(INITIAL_DATA)

export const ListFieldsProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const [isZoomedOut, setIsZoomedOut] = useState(false)
  const [selectedFieldId, setSelectedFieldId] = useState('')
  const [headerData, setHeaderData] = useState<Header['data']>(INITIAL_DATA.header.data)
  const [errorState, setErrorState] = useState<Omit<ListFieldsContextType['error'], 'set'>>({
    value: INITIAL_DATA.error.value,
    refetch: INITIAL_DATA.error.refetch,
  })

  const setSort = useCallback((option: CustomerSortOptions): void => {
    setHeaderData((prev) => ({ ...prev, sort: option }))
  }, [])

  const setSearch = useCallback(
    (search: string): void => {
      if (search !== headerData.search) {
        setHeaderData((prev) => ({ ...prev, search }))
      }
    },
    [headerData.search],
  )

  const setContextError = useCallback((error: ListFieldsErrorState) => {
    setErrorState(error)
  }, [])

  const contextValue = React.useMemo(
    () => ({
      header: {
        data: headerData,
        setters: {
          setSort,
          setSearch,
        },
      },
      error: {
        value: errorState.value,
        refetch: errorState.refetch,
        set: setContextError,
      },
      setSelectedFieldId: setSelectedFieldId,
      selectedFieldId: selectedFieldId,
      isZoomedOut: isZoomedOut,
      setIsZoomedOut: setIsZoomedOut,
    }),
    [
      headerData,
      setSort,
      setSearch,
      errorState.value,
      errorState.refetch,
      setContextError,
      selectedFieldId,
      setIsZoomedOut,
      isZoomedOut,
    ],
  )

  return <ListFieldsContext.Provider value={contextValue}>{children}</ListFieldsContext.Provider>
}
